import React from 'react';
import { createRoot } from 'react-dom/client';

import { Spinner } from '../components/helpers/Spinner';
import { getCode } from '../middleware/activate';

/**
 * Download file by custom URl with parameters.
 *
 * @param {URLMaker|string} url URL for download.
 * @param {string} method Method (get, post).
 * @param {object} params Optional URL parameters.
 * @param {object} body Optional body params.
 * @param {Function} onResponseJSON Optional callback on json.
 * @param {Function} afterSuccess Optional callback after successful request.
 * @param {object} headers Optional headers.
 * @param {string|null} name Optional file name.
 * @param {boolean} noAuth Do not send Authorization in the headers.
 */
export default async function downloadFile({
  url,
  method = 'GET',
  params = {},
  body: postBody = {},
  headers = {},
  onResponseJSON = () => {},
  onError = () => {},
  afterSuccess = () => {},
  name = null,
  noAuth = false,
}) {
  const upperMethod = method.toUpperCase();

  const searchParams = new URLSearchParams(params).toString();
  const fetchUrl = searchParams ? `${url}?${searchParams}` : url;

  insertSpinner();

  try {
    const response = await fetch(fetchUrl, {
      method: upperMethod,
      headers: {
        ...(upperMethod === 'POST' && { 'Content-Type': 'application/json' }),
        ...headers,
        ...(!noAuth && { Authorization: `Bearer ${getCode()}` }),
      },
      ...(upperMethod === 'POST' && { body: JSON.stringify(postBody) }),
    });

    if (!response.ok) {
      let errorData = null;

      try {
        const contentType = response.headers.get('content-type') || '';

        if (contentType.includes('application/json')) {
          errorData = await response.json();
        } else {
          errorData = await response.text();
        }
      } catch (error) {
        errorData = null;
      }

      console.error('HTTP error', response.status, errorData);

      onError({
        status: response.status,
        data: errorData,
      });

      return;
    }

    const contentType = response.headers.get('content-type') || '';

    if (contentType.includes('application/json')) {
      const json = await response.json();

      onResponseJSON(json);

      return json;
    }

    let fileName = name || 'file';

    const contentDisposition = response.headers.get('content-disposition');

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?(.+?)"?$/);

      if (match) fileName = match[1];
    }

    const blob = await response.blob();

    // Native download.
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(blobUrl);

    afterSuccess();

    return blob;
  } catch (error) {
    console.error('Download error:', error);
    throw error;
  } finally {
    removeSpinner();
  }
}

let spinner = null;

function insertSpinner() {
  if (spinner) return;

  const container = document.createElement('div');
  const root = createRoot(container);

  document.body.appendChild(container);
  root.render(<Spinner className="global-spinner" />);
  spinner = { container, root };
}

function removeSpinner() {
  if (!spinner) return;

  spinner.root.unmount();
  spinner.container.remove();
  spinner = null;
}

/**
 * Download Browse page file by params.
 *
 * @param {string} url URL for request.
 * @param {object} params Params for request.
 */
export function downloadBrowseFile(url, params) {
  downloadFile({
    url,
    headers: {
      'content-type': 'application/json',
    },
    method: 'post',
    body: {
      params,
    },
  });
}

/**
 * Download html content.
 *
 * @param {string} htmlContent Html content.
 * @param {string} name File name.
 */
export function downloadHtml(htmlContent, name = Date.now()) {
  const blob = new Blob([htmlContent], { type: 'text/plain' });
  const link = document.createElement('a');

  document.body.appendChild(link);
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', `${name}.html`);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}
